import { InstrumentCondition, MarketType, PEARL_SPACING, Side, Status, TickMath } from '@synfutures/sdks-perp';
// import BN from 'bignumber.js';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getOracleConfig } from '@/configs';
import { CHAIN_ID } from '@/constants/chain';
import { MMR_RATIO, PAIR_RATIO_BASE } from '@/constants/global';
import { WrappedBigNumber, WrappedBigNumberLike } from '@/entities/WrappedBigNumber';
import { WrappedPair } from '@/entities/WrappedPair';
import { useAppSelector } from '@/hooks';
import { useChainId } from '@/hooks/web3/useChain';
import { Context } from '@derivation-tech/context';

import { IChainPairsFavorites, IMetaPair, IOracleInfo } from '@/types/pair';
import { TokenInfo } from '@/types/token';
import { getChainIdByChainShortName } from '@/utils/chain';
import { formatNumber, inputNumChecker } from '@/utils/numberUtil';
import { getDisplayExpiry, isDisableExpiredPair, isPairExpiredToday } from '@/utils/pairs';

import { FETCHING_STATUS } from '@/constants';
import { mappingToken } from '@/utils/token';
import { getLimitAlignPriceWad } from '@/utils/trade';
import { getPairId } from '@/utils/transform/transformId';
import { useBackendChainConfig } from '../config/hook';
import {
  useMarketPairInfo,
  useMarketWrappedInstrument,
  useWrappedInstrument,
  useWrappedInstrumentMap,
} from '../futures/hooks';
import { useTokenPrices } from '../global/query';
import { useCurrentMarketPairFromUrl, useDefaultPairSymbolFromUrlOrFallbackLocalOrApi } from '../market/hooks';
import { selectChainMetaPair, selectChainMetaPairStatus, selectChainPairFavorites, selectMetaPair } from './pairSlice';
// import { useTokenPriceInfoMap } from '../global/hooks';

export function useMetaPairMap(chainId: number | undefined): Record<string, IMetaPair> {
  const pairs = useAppSelector(selectChainMetaPair(chainId));
  return useMemo(() => pairs || {}, [pairs]);
}
export function useMetaPairMapStatus(chainId: number | undefined): FETCHING_STATUS {
  const status = useAppSelector(selectChainMetaPairStatus(chainId));
  return useMemo(() => status || FETCHING_STATUS.INIT, [status]);
}

export function useMetaPair(chainId: CHAIN_ID | undefined, pairId: string | undefined): IMetaPair | undefined {
  const pair = useAppSelector(selectMetaPair(chainId, pairId));
  return pair;
}

export function useWrappedPairMap(chainId: CHAIN_ID | undefined): { [pairId: string]: WrappedPair } {
  const originPairMap = useMetaPairMap(chainId);

  const futuresCoreMap = useWrappedInstrumentMap(chainId);
  const { data: priceMap } = useTokenPrices({ chainId });

  return useMemo(() => {
    if (!chainId || !futuresCoreMap) return {};
    return _.mapValues(originPairMap, (originPair) => {
      let pair = WrappedPair.getInstance(originPair.id, chainId);
      if (futuresCoreMap[originPair.instrumentId]) {
        if (!pair) {
          pair = WrappedPair.wrapInstance({
            metaPair: originPair,
            rootInstrument: futuresCoreMap[originPair.instrumentId],
          });
          // TODO uncomment this later
          // pair && futuresCoreMap[originPair.instrumentId].setPair(pair.expiry, pair);
        } else {
          pair.fillField(originPair);
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return pair!;
    });
    // must include pricemap
  }, [chainId, originPairMap, futuresCoreMap, priceMap]);
}

export function useWrappedPairList(chainId: CHAIN_ID | undefined): WrappedPair[] {
  const pairMap = useWrappedPairMap(chainId);
  return useMemo(() => {
    return Object.values(pairMap || {});
  }, [pairMap]);
}

export function useDisplayPairList(chainId: CHAIN_ID | undefined, isFilterAbnormalPair = true): WrappedPair[] {
  const pairList = useWrappedPairList(chainId);
  // const tokenPriceMap = useTokenPriceInfoMap(chainId);

  const displayList = useMemo(() => {
    return pairList.filter((pair) => {
      return isFilterAbnormalPair
        ? pair?.rootInstrument?.condition === InstrumentCondition.NORMAL && pair?.status !== Status.SETTLED
        : true;
    });
  }, [isFilterAbnormalPair, pairList]);

  return useMemo(() => displayList.filter((p) => !!p), [displayList]);
}
export function useDisplayPairMargins(chainId: CHAIN_ID | undefined): string[] {
  const pairs = useDisplayPairList(chainId);
  return useMemo(() => {
    const symbols = pairs.map((p) => p.rootInstrument?.marginToken.symbol);
    return _.uniq(symbols) as string[];
  }, [pairs]);
}

// export function useChainPairStats(chainId: CHAIN_ID | undefined): ListStatus<WrappedPairData> {
//   const chainPairStats = useAppSelector(selectChainPairStats);

//   return useMemo(() => {
//     if (chainId) {
//       const pairStats = _.get(chainPairStats, [chainId]);
//       if (pairStats) return pairStats;
//     }

//     return {} as ListStatus<WrappedPairData>;
//   }, [chainId, chainPairStats]);
// }
// export function usePairStatsMap(chainId: CHAIN_ID | undefined): { [pairId: string]: WrappedPairData } {
//   const chainPairStats = useChainPairStats(chainId);
//   return useMemo(() => _.get(chainPairStats, ['list']), [chainPairStats]);
// }
// export function usePairStats(pairId: string | undefined, chainId: CHAIN_ID | undefined): WrappedPairData | undefined {
//   // const chainPairStats = useChainPairStats();
//   const singlePairStats = useAppSelector(selectSinglePairStats(chainId, pairId));

//   const pair = useWrappedPair(chainId, pairId);
//   // const singlePairStats = useMemo(() => _.get(chainPairStats, ['list', pairId || '']), [chainPairStats, pairId]);
//   useEffect(() => {
//     pair && singlePairStats && pair.setPairStats(new WrappedPairData(singlePairStats));
//   }, [pair, singlePairStats]);

//   return singlePairStats;
// }

export function useWrappedPair(chainId: CHAIN_ID | undefined, pairId: string | undefined): WrappedPair | undefined {
  const metaPair = useMetaPair(chainId, pairId);
  const wrappedFutures = useWrappedInstrument(chainId, metaPair?.instrumentId);
  // const { data: priceMap } = useTokenPrices({ chainId });
  const pairFromApi = useCurrentMarketPairFromUrl(chainId);

  const marketWrappedInstrument = useMarketWrappedInstrument(chainId || undefined, pairFromApi?.instrumentAddr);

  const currentPair = useMemo(() => {
    if (marketWrappedInstrument?.pairMap && pairId) {
      const pairs = Object.values(marketWrappedInstrument?.pairMap);
      return [...pairs].find((pair) => pair.id === pairId);
    }
  }, [marketWrappedInstrument?.pairMap, pairId]);

  const pair = useMemo(() => {
    if (pairId && currentPair && currentPair.id === pairId) {
      return currentPair;
    }
    if (metaPair && wrappedFutures) {
      let pair = WrappedPair.getInstance(metaPair.id, chainId);
      if (!pair) pair = WrappedPair.wrapInstance({ metaPair, rootInstrument: wrappedFutures });
      else {
        pair.fillField(metaPair);
      }
      return pair;
    }
    return undefined;
    // must include priceMap
  }, [metaPair, wrappedFutures, pairId, currentPair, chainId]);
  return pair;
}

export function useCurrentPairFromUrl(
  chainId: number | undefined,
  isFilterAbnormalPair = true,
): WrappedPair | undefined {
  const { pairSymbol, chainShortName } = useParams();
  const chainIdFromUrl = useMemo(() => {
    return chainShortName ? getChainIdByChainShortName(chainShortName) : undefined;
  }, [chainShortName]);
  const symbolInStorage = useDefaultPairSymbolFromUrlOrFallbackLocalOrApi(chainId);
  const pairList = useDisplayPairList(chainId, isFilterAbnormalPair);
  const dormantPairList = useDormantPair(chainId);
  const pair = useMemo(() => {
    if (chainIdFromUrl !== chainId) return undefined;

    const symbol = pairSymbol || symbolInStorage;
    return [...pairList, ...dormantPairList].find((wp) => wp.symbol === symbol);
  }, [chainId, chainIdFromUrl, dormantPairList, pairList, pairSymbol, symbolInStorage]);
  // useEffect(() => {
  //   pair && console.log('🚩useCurrentPairFromUrl----> current pair', pairSymbol, pair);
  // }, [pairSymbol]);

  const currentPairFromApi = useCurrentWrappedPairFromApi(chainId);

  const currPairInfo = useMarketPairInfo(chainId, pair?.instrumentAddr, pair?.expiry);

  return useMemo(() => {
    const current = currentPairFromApi || pair;

    currPairInfo && current?.setPairStats(currPairInfo);
    return current;
  }, [currPairInfo, currentPairFromApi, pair]);
}

export function useCurrentWrappedPairFromApi(chainId: number | undefined): WrappedPair | undefined {
  const { pairSymbol, chainShortName } = useParams();
  const pairFromApi = useCurrentMarketPairFromUrl(chainId);
  const chainIdFromUrl = useMemo(() => {
    return chainShortName ? getChainIdByChainShortName(chainShortName) : undefined;
  }, [chainShortName]);
  const symbolInStorage = useDefaultPairSymbolFromUrlOrFallbackLocalOrApi(chainId);

  const dormantPairList = useDormantPair(chainId);

  const marketWrappedInstrument = useMarketWrappedInstrument(chainIdFromUrl || undefined, pairFromApi?.instrumentAddr);

  const currentPair = useMemo(() => {
    if (marketWrappedInstrument?.pairMap) {
      const pairs = Object.values(marketWrappedInstrument?.pairMap);
      const symbol = pairSymbol || symbolInStorage;
      return [...pairs, ...dormantPairList].find((pair) => pair.symbol === symbol);
    }
  }, [dormantPairList, marketWrappedInstrument?.pairMap, pairSymbol, symbolInStorage]);

  return currentPair;
}

export function useCurrentPairByDevice(chainId: number | undefined): WrappedPair | undefined {
  const currentPairFromUrl = useCurrentPairFromUrl(chainId);
  const pair = useMemo(() => currentPairFromUrl, [currentPairFromUrl]);
  return pair;
}

export function useCombinedPairFromUrl(
  chainId: number | undefined,
  isFilterAbnormalPair = true,
): WrappedPair | undefined {
  const pair = usePairFromUrl(chainId, isFilterAbnormalPair);

  return pair;
}
export function usePairFromUrl(chainId: number | undefined, isFilterAbnormalPair = true): WrappedPair | undefined {
  // const { isMobile } = useMediaQueryDevice();
  const pair = useCurrentPairFromUrl(chainId, isFilterAbnormalPair);
  return useMemo(() => {
    if (pair && !pair.isNormalPair) {
      return undefined;
    }
    return pair;
  }, [pair?.isNormalPair, pair?.id]);
}

export function useChainPairFavorites(): IChainPairsFavorites {
  return useAppSelector(selectChainPairFavorites);
}

export function useDormantPair(chainId: CHAIN_ID | undefined): WrappedPair[] {
  const wrappedPairList = useWrappedPairList(chainId);

  return useMemo(() => {
    const unInitiatedPairs = wrappedPairList.filter(
      (p) =>
        p.rootInstrument.condition === InstrumentCondition.NORMAL &&
        p.liquidity.eq(0) &&
        p?.status === Status.DORMANT &&
        // filter expired pair
        !isDisableExpiredPair(p) &&
        !isPairExpiredToday(p.expiry),
    );
    return unInitiatedPairs;
  }, [wrappedPairList]);
}

export function usePairInfo(pair: WrappedPair | undefined):
  | {
      displayBaseToken: TokenInfo;
      displayQuoteToken: TokenInfo;
      marginToken: TokenInfo;
      expiry: string;
      minQuoteTradeSize: WrappedBigNumber;
      imrRate: WrappedBigNumber;
      mmrRate: WrappedBigNumber;
      tip: WrappedBigNumber;
      tradingFeeRatio: WrappedBigNumber;
      protocolFeeRatio: WrappedBigNumber;
      oracleInfo: IOracleInfo;
    }
  | undefined {
  const oracleConfigConfig = useMemo(() => {
    return getOracleConfig();
  }, []);
  const pairInfo = useMemo(() => {
    if (pair) {
      const displayBaseToken = pair.rootInstrument.displayBaseToken;
      const displayQuoteToken = pair.rootInstrument.displayQuoteToken;
      const marginToken = pair.rootInstrument.marginToken;
      const expiry = getDisplayExpiry(pair.expiry);
      const imrRate = WrappedBigNumber.from(pair.rootInstrument.setting.initialMarginRatio).div(PAIR_RATIO_BASE);
      const minQuoteTradeSize = WrappedBigNumber.from(pair.rootInstrument.setting.quoteParam.minMarginAmount).div(
        imrRate,
      );
      const mmrRate = WrappedBigNumber.from(pair.rootInstrument.setting.maintenanceMarginRatio).div(PAIR_RATIO_BASE);
      const tip = WrappedBigNumber.from(pair.rootInstrument.setting.quoteParam.tip);
      const tradingFeeRatio = WrappedBigNumber.from(pair.rootInstrument.setting.quoteParam.tradingFeeRatio).div(
        PAIR_RATIO_BASE,
      );
      const protocolFeeRatio = WrappedBigNumber.from(pair.rootInstrument.setting.quoteParam.protocolFeeRatio).div(
        PAIR_RATIO_BASE,
      );
      const oracleInfo = oracleConfigConfig[pair.rootInstrument.marketType];
      return {
        displayBaseToken,
        displayQuoteToken,
        marginToken,
        expiry,
        minQuoteTradeSize,
        imrRate,
        mmrRate,
        tip,
        tradingFeeRatio,
        protocolFeeRatio,
        oracleInfo,
      };
    }
  }, [oracleConfigConfig, pair]);
  return pairInfo;
}
export function useGetAlignedPrice(pair: WrappedPair | undefined): (
  price: WrappedBigNumberLike,
  side: Side,
) =>
  | {
      tick: number;
      originPrice: WrappedBigNumber;
      adjustPrice: WrappedBigNumber;
    }
  | undefined {
  const getAlignPriceWad = useCallback(
    (
      price: WrappedBigNumberLike,
      side: Side,
    ):
      | {
          tick: number;
          originPrice: WrappedBigNumber;
          adjustPrice: WrappedBigNumber;
        }
      | undefined => {
      try {
        if (pair) {
          const originPrice = WrappedBigNumber.from(price || 0);
          // const adjustedSide = getAdjustTradeSide(side, pair.isInverse);
          const adjustedSide = side;
          const result = getLimitAlignPriceWad({
            limitPrice: originPrice,
          });
          if (result) {
            if (
              inputNumChecker(WrappedBigNumber.from(price).stringValue, pair.priceDecimal) !==
              inputNumChecker(result.adjustPrice.stringValue, pair.priceDecimal)
            ) {
              let tick = result?.tick;
              let adjustPrice = result?.adjustPrice;
              // can not place order on current amm tick
              if (adjustedSide === Side.LONG && result?.tick >= pair.tick) {
                tick = tick - PEARL_SPACING;
                // const tickPrice = await sdkContext?.perp?.calc?.getWadAtTick(pair.instrumentAddr, tick);
                const tickPrice = WrappedBigNumber.from(TickMath.getWadAtTick(tick));
                adjustPrice = tickPrice;
              }
              if (adjustedSide === Side.SHORT) {
                tick = tick + PEARL_SPACING;
                // const tickPrice = await sdkContext?.perp?.calc?.getWadAtTick(pair.instrumentAddr, tick);
                const tickPrice = WrappedBigNumber.from(TickMath.getWadAtTick(tick));
                adjustPrice = tickPrice;
              }
              return { ...result, tick, adjustPrice };
            }
            return { ...result, adjustPrice: originPrice };
          }
        }
        return undefined;
      } catch (e) {
        console.log('🚀 ~ file: index.tsx:76 ~ e:', e);
        return undefined;
      }
    },
    [pair],
  );

  return getAlignPriceWad;
}
export function usePairAdjustedRange(
  pair: WrappedPair | undefined,
  tradeSide: Side,
  isTurnOnCrossMarket?: boolean,
): (WrappedBigNumber | undefined)[] {
  return useMemo(() => {
    if (pair) {
      const { lowerTick, upperTick } = pair.placeOrderLimit;
      const markPrice = pair.wrapAttribute('markPrice');

      const result = {
        lowerPrice: TickMath.getWadAtTick(lowerTick),
        upperPrice: TickMath.getWadAtTick(upperTick),
      };

      const { lowerPrice: lowerLimit, upperPrice: upperLimit } = result;

      // mmr limit for cross market
      const mmrRatio = pair?.rootInstrument.setting.maintenanceMarginRatio / PAIR_RATIO_BASE;

      let upperMaxLimit = WrappedBigNumber.from(upperLimit);
      if (isTurnOnCrossMarket && tradeSide === Side.LONG) {
        upperMaxLimit = markPrice.mul(1 + mmrRatio);
      }

      let lowerMaxLimit = WrappedBigNumber.from(lowerLimit);
      if (isTurnOnCrossMarket && tradeSide === Side.SHORT) {
        lowerMaxLimit = markPrice.mul(1 - mmrRatio);
      }
      if (upperMaxLimit && lowerMaxLimit) {
        return [
          WrappedBigNumber.from(
            formatNumber(
              lowerMaxLimit.stringValue,
              pair.priceDecimal,
              false,
              // pair.isInverse ? BN.ROUND_CEIL : BN.ROUND_DOWN,
            ),
          ),
          WrappedBigNumber.from(
            formatNumber(
              upperMaxLimit.stringValue,
              pair.priceDecimal,
              false,
              // pair.isInverse ? BN.ROUND_CEIL : BN.ROUND_DOWN,
            ),
          ),
        ];
      }
      return [];
    }

    return [];
  }, [pair, isTurnOnCrossMarket, tradeSide]);
}
export function useGetAlignedFairPrice(pair: WrappedPair | undefined): (side: Side) => WrappedBigNumber | undefined {
  const getAlignedFairPrice = useCallback(
    (side: Side) => {
      try {
        if (pair?.fairPrice) {
          let newTick = Math.floor(pair?.tick / PEARL_SPACING) * PEARL_SPACING;
          if (pair?.isInverse) {
            if (side == Side.LONG) {
              newTick = newTick + PEARL_SPACING;
            }
            if (side === Side.SHORT) {
              if (newTick === pair?.tick) {
                newTick = newTick - PEARL_SPACING;
              }
            }
          } else {
            if (side == Side.LONG) {
              if (newTick === pair?.tick) {
                newTick = newTick - PEARL_SPACING;
              }
            }
            if (side === Side.SHORT) {
              newTick = newTick + PEARL_SPACING;
            }
          }

          const adjustPrice = WrappedBigNumber.from(TickMath.getWadAtTick(newTick));

          if (adjustPrice) {
            return WrappedBigNumber.from(
              formatNumber(
                adjustPrice.stringValue,
                pair.priceDecimal,
                false,
                // side === Side.LONG ? BN.ROUND_DOWN : BN.ROUND_CEIL,
              ),
            );
          }
        }
      } catch (error) {
        console.log('🚀 ~ useGetAlignedFairPrice ~ error:', error);
      }
    },
    [pair?.fairPrice, pair?.isInverse, pair?.priceDecimal, pair?.tick],
  );

  return getAlignedFairPrice;
}
export function usePairLimitPriceRange(
  pair: WrappedPair | undefined,
  side: Side,
  isTurnOnCrossMarket?: boolean,
): [WrappedBigNumber | undefined, WrappedBigNumber | undefined] {
  const getAlignedFairPrice = useGetAlignedFairPrice(pair);
  const [adjustedLowerPrice, adjustedUpperPrice] = usePairAdjustedRange(pair, side, isTurnOnCrossMarket);
  const alignedFairPrice = useMemo(() => {
    if (!pair?.fairPrice) return undefined;
    const price = getAlignedFairPrice(side);

    return price;
  }, [pair?.fairPrice._hex, pair?.markPrice._hex, side]);
  return useMemo(() => {
    const adjustSide = side;
    if (adjustedLowerPrice && adjustedUpperPrice && alignedFairPrice) {
      if (adjustSide === Side.LONG) {
        return [adjustedLowerPrice, isTurnOnCrossMarket ? adjustedUpperPrice : alignedFairPrice];
      }
      return [isTurnOnCrossMarket ? adjustedLowerPrice : alignedFairPrice, adjustedUpperPrice];
    }
    return [undefined, undefined];
    //  must depend on stringValue
  }, [
    adjustedLowerPrice?.stringValue,
    adjustedUpperPrice?.stringValue,
    alignedFairPrice?.stringValue,
    isTurnOnCrossMarket,
    side,
  ]);
}
export function usePairLimitPriceRangeTicks(
  pair: WrappedPair | undefined,
  side: Side,
  sdkContext: Context | undefined,
  isTurnOnCrossMarket?: boolean,
): number[] {
  const getAlignPriceWad = useGetAlignedPrice(pair);
  const [lowerUpperTicks, setLowerUpperTicks] = useState<number[]>([]);
  const [lowerPrice, upperPrice] = usePairLimitPriceRange(pair, side, isTurnOnCrossMarket);
  const getUpperLowerTicks = useCallback(async () => {
    if (upperPrice && lowerPrice) {
      const alignedUpper = getAlignPriceWad(upperPrice?.stringValue, side);
      const alignedLower = getAlignPriceWad(lowerPrice?.stringValue, side);
      if (alignedLower && alignedUpper) {
        const sign = pair?.isInverse ? -1 : 1;
        const lowerTick = alignedLower?.tick * sign;
        const upperTick = alignedUpper?.tick * sign;

        setLowerUpperTicks(side === Side.SHORT ? [lowerTick, upperTick] : [lowerTick, upperTick]);
      }
    }
  }, [upperPrice, lowerPrice, getAlignPriceWad, side, pair?.isInverse]);
  useEffect(() => {
    getUpperLowerTicks();
  }, [getUpperLowerTicks]);
  return lowerUpperTicks;
}

export function useGetDefaultLimitPrice(
  currentPair: WrappedPair | undefined,
  side: Side,
): (tradeSide?: Side) => { tick: number; adjustPrice: WrappedBigNumber } | undefined {
  const getAlignPriceWad = useGetAlignedPrice(currentPair);
  const getDefaultLimitPrice = useCallback(
    (tradeSide?: Side): { tick: number; adjustPrice: WrappedBigNumber } | undefined => {
      if (currentPair?.fairPrice) {
        const adjustSide = tradeSide || side;
        const alignedPrice = getAlignPriceWad(
          currentPair
            ?.wrapAttribute('fairPrice')
            .mul(
              1 +
                ((MMR_RATIO * currentPair.rootInstrument.metaFutures.setting.maintenanceMarginRatio) /
                  PAIR_RATIO_BASE) *
                  (adjustSide === Side.SHORT ? 1 : -1),
            ),
          tradeSide || side,
        );
        if (alignedPrice) {
          return alignedPrice;
        }
      }
    },
    [currentPair, side, getAlignPriceWad],
  );

  return getDefaultLimitPrice;
}

export function usePairDisabledForLimit(pair: WrappedPair | undefined): {
  inDisabledPairs: boolean | undefined;
  pairDisabledInLimit: boolean | undefined;
} {
  const chainId = useChainId();
  const dappConfig = useBackendChainConfig(chainId);
  const inDisabledPairs = useMemo(
    // read from contract to check if pair is disabled
    () => !!pair?.symbol && (!pair?.withinDeviationLimit || pair?.rootInstrument?.placePaused),
    [pair?.symbol, pair?.withinDeviationLimit, pair?.rootInstrument?.placePaused],
  );
  const pairDisabledInLimit = useMemo(
    () => dappConfig?.trade.isDisableLimitOrder || inDisabledPairs,
    [dappConfig?.trade.isDisableLimitOrder, inDisabledPairs],
  );

  return { inDisabledPairs, pairDisabledInLimit };
}

export function useCurrentPairBaseInfoFromUrl(
  chainId: number | undefined,
  isFilterAbnormalPair = true,
):
  | {
      id: string;
      chainId: CHAIN_ID;
      instrumentAddr: string;
      expiry: number;
      symbol: string;
      rootInstrument: {
        marketType: MarketType;
        instrumentAddr: string;
        id: string;
        baseToken: TokenInfo;
        quoteToken: TokenInfo;
      };
    }
  | undefined {
  const pairFromChain = useCurrentPairFromUrl(chainId, isFilterAbnormalPair);
  const pairFromApi = useCurrentMarketPairFromUrl(chainId);
  return useMemo(() => {
    if (!chainId) return;
    if (pairFromChain) {
      return pairFromChain;
    }
    if (pairFromApi)
      return {
        chainId: chainId,
        id: getPairId(pairFromApi?.instrumentAddr, pairFromApi?.expiry),
        instrumentAddr: pairFromApi?.instrumentAddr,
        expiry: pairFromApi?.expiry,
        symbol: pairFromApi?.symbol,
        rootInstrument: {
          marketType: pairFromApi?.marketType,
          instrumentAddr: pairFromApi?.instrumentAddr,
          id: pairFromApi?.instrumentAddr,
          baseToken: mappingToken({
            token: pairFromApi?.baseToken,
            marketType: pairFromApi?.marketType,
            chainId: chainId,
          }),
          quoteToken: mappingToken({
            token: pairFromApi?.quoteToken,
            marketType: pairFromApi?.marketType,
            chainId: chainId,
          }),
        },
      };
  }, [chainId, pairFromApi, pairFromChain]);
}
